import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { dashboardAPI } from '../lib/api';
import { formatCurrency } from '../lib/utils';
import { useAuth } from '../lib/auth';
import {
  ArrowLeftRight, Receipt, Send, Landmark, CreditCard, ShieldCheck,
  TrendingUp, ArrowUpRight, ArrowDownRight, Wallet, PiggyBank,
  BarChart3, Clock, ChevronRight
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Skeleton } from '../components/ui/skeleton';
import { toast } from 'sonner';

const quickActions = [
  { label: 'Transfer', icon: ArrowLeftRight, path: '/transfer', testId: 'quick-action-transfer', color: '#0B5CAB' },
  { label: 'Pay Bills', icon: Receipt, path: '/bill-pay', testId: 'quick-action-bill-pay', color: '#168A4A' },
  { label: 'Zelle', icon: Send, path: '/zelle', testId: 'quick-action-zelle', color: '#6B21A8' },
  { label: 'Wire', icon: Landmark, path: '/wire', testId: 'quick-action-wire', color: '#0A4E93' },
  { label: 'Cards', icon: CreditCard, path: '/cards', testId: 'quick-action-cards', color: '#B45309' },
  { label: 'Social Security', icon: ShieldCheck, path: '/social-security', testId: 'quick-action-social-security', color: '#DC2626' },
];

export default function DashboardPage() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();
  const { user } = useAuth();

  useEffect(() => {
    loadDashboard();
  }, []);

  const loadDashboard = async () => {
    try {
      const res = await dashboardAPI.overview();
      setData(res.data);
    } catch (err) {
      toast.error('Failed to load dashboard');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="page-container">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
          {[1,2,3].map(i => <Skeleton key={i} className="h-40 rounded-xl" />)}
        </div>
        <Skeleton className="h-20 rounded-xl mb-6" />
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
          <Skeleton className="h-60 rounded-xl lg:col-span-2" />
          <Skeleton className="h-60 rounded-xl" />
        </div>
      </div>
    );
  }

  const accounts = data?.accounts || [];
  const cards = data?.cards || [];
  const recentTxns = data?.recent_transactions || [];
  const fxRates = data?.fx_rates || [];

  const checking = accounts.find(a => a.type === 'checking');
  const savings = accounts.find(a => a.type === 'savings');
  const investment = accounts.find(a => a.type === 'investment');

  return (
    <div className="page-container">
      {/* Welcome section */}
      <div className="mb-6">
        <h2 className="text-2xl font-semibold tracking-tight" style={{ fontFamily: 'Space Grotesk' }}>Good {getGreeting()}, {user?.full_name || user?.username}</h2>
        <p className="text-sm text-muted-foreground mt-1">Here's your financial overview</p>
      </div>

      {/* Account Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
        {/* Checking */}
        {checking && (
          <div className="account-card" data-testid="account-card-checking">
            <div className="flex items-start justify-between">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl bg-blue-50 flex items-center justify-center">
                  <Wallet className="h-5 w-5 text-[#0B5CAB]" />
                </div>
                <div>
                  <p className="text-sm font-medium text-muted-foreground">Primary Checking</p>
                  <p className="text-xs text-muted-foreground">{checking.account_number}</p>
                </div>
              </div>
              <Badge variant="secondary" className="text-xs">Active</Badge>
            </div>
            <div className="mt-4">
              <p className="text-xs text-muted-foreground">Available Balance</p>
              <p className="text-2xl font-semibold tabular-nums tracking-tight" data-testid="account-card-checking-balance">
                {formatCurrency(checking.available_balance)}
              </p>
            </div>
            <div className="mt-3 flex gap-2">
              <Button variant="outline" size="sm" className="text-xs" onClick={() => navigate('/transfer')}>Transfer</Button>
              <Button variant="outline" size="sm" className="text-xs" onClick={() => navigate('/transactions')}>History</Button>
            </div>
          </div>
        )}

        {/* Savings */}
        {savings && (
          <div className="account-card" data-testid="account-card-savings">
            <div className="flex items-start justify-between">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl bg-green-50 flex items-center justify-center">
                  <PiggyBank className="h-5 w-5 text-green-600" />
                </div>
                <div>
                  <p className="text-sm font-medium text-muted-foreground">Savings Account</p>
                  <p className="text-xs text-muted-foreground">{savings.account_number}</p>
                </div>
              </div>
              <Badge variant="secondary" className="text-xs">{savings.interest_rate}% APY</Badge>
            </div>
            <div className="mt-4">
              <p className="text-xs text-muted-foreground">Available Balance</p>
              <p className="text-2xl font-semibold tabular-nums tracking-tight" data-testid="account-card-savings-balance">
                {formatCurrency(savings.available_balance)}
              </p>
            </div>
            <div className="mt-3 flex gap-2">
              <Button variant="outline" size="sm" className="text-xs" onClick={() => navigate('/transfer')}>Transfer</Button>
              <Button variant="outline" size="sm" className="text-xs" onClick={() => navigate('/transactions')}>History</Button>
            </div>
          </div>
        )}

        {/* Investment */}
        {investment && (
          <div className="account-card" data-testid="account-card-investment">
            <div className="flex items-start justify-between">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl bg-purple-50 flex items-center justify-center">
                  <BarChart3 className="h-5 w-5 text-purple-600" />
                </div>
                <div>
                  <p className="text-sm font-medium text-muted-foreground">Investment Account</p>
                  <p className="text-xs text-muted-foreground">{investment.account_number}</p>
                </div>
              </div>
              <Badge className="bg-green-50 text-green-700 border-green-200 text-xs">+8.45% YTD</Badge>
            </div>
            <div className="mt-4">
              <p className="text-xs text-muted-foreground">Total Value</p>
              <p className="text-2xl font-semibold tabular-nums tracking-tight" data-testid="account-card-investment-balance">
                {formatCurrency(investment.available_balance)}
              </p>
            </div>
            <div className="mt-3 flex gap-2">
              <Button variant="outline" size="sm" className="text-xs" onClick={() => navigate('/accounts')}>Details</Button>
            </div>
          </div>
        )}
      </div>

      {/* Quick Actions */}
      <div className="mb-6">
        <h3 className="text-sm font-semibold tracking-tight mb-3" style={{ fontFamily: 'Space Grotesk' }}>Quick Actions</h3>
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
          {quickActions.map(action => (
            <button
              key={action.label}
              data-testid={action.testId}
              onClick={() => navigate(action.path)}
              className="quick-action-btn"
            >
              <div className="icon-wrap" style={{ background: action.color }}>
                <action.icon className="h-4 w-4" />
              </div>
              <span className="text-xs font-medium">{action.label}</span>
            </button>
          ))}
        </div>
      </div>

      {/* Bottom row: Cards + FX + Investment */}
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-4">
        {/* Credit Cards */}
        <div className="lg:col-span-5">
          <h3 className="text-sm font-semibold tracking-tight mb-3" style={{ fontFamily: 'Space Grotesk' }}>Your Cards</h3>
          <div className="space-y-3">
            {cards.map((card) => (
              <div key={card._id} className={`credit-card ${card.type}`} data-testid={`credit-card-${card.type === 'visa' ? 'visa-platinum' : card.type === 'mastercard' ? 'mastercard-cash-rewards' : 'amex-unlimited'}`}>
                <div className="flex items-start justify-between relative z-10">
                  <p className="text-xs uppercase tracking-widest text-white/70">{card.name}</p>
                  <p className="text-xs text-white/60">{card.type === 'visa' ? 'VISA' : card.type === 'mastercard' ? 'MASTERCARD' : 'AMEX'}</p>
                </div>
                <div className="relative z-10">
                  <p className="font-mono text-sm text-white/85 mt-4">**** **** **** {card.last_four}</p>
                  <p className="mt-3 text-sm font-semibold tracking-wide" data-testid="credit-card-cardholder-name">{card.cardholder_name}</p>
                </div>
                <div className="flex items-end justify-between mt-3 relative z-10">
                  <div>
                    <p className="text-xs text-white/70">Credit Limit</p>
                    <p className="text-base font-semibold tabular-nums">
                      {card.credit_limit >= 999999999 ? 'Unlimited' : formatCurrency(card.credit_limit)}
                    </p>
                  </div>
                  <p className="text-xs text-white/70">EXP {card.expiry}</p>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* FX Rates + Investment */}
        <div className="lg:col-span-4">
          {/* Investment Widget */}
          <div className="investment-widget mb-4" data-testid="investment-widget">
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-sm font-semibold tracking-tight" style={{ fontFamily: 'Space Grotesk' }}>Investment Account</h3>
              <TrendingUp className="h-4 w-4 text-green-600" />
            </div>
            {investment && (
              <div>
                <p className="text-2xl font-semibold tabular-nums">{formatCurrency(investment.available_balance)}</p>
                <div className="flex items-center gap-4 mt-3">
                  <div>
                    <p className="text-xs text-muted-foreground">Today's Deposit</p>
                    <p className="text-sm font-semibold text-green-600 tabular-nums" data-testid="investment-todays-deposit">
                      <ArrowUpRight className="h-3 w-3 inline" /> +{formatCurrency(investment.todays_deposit)}
                    </p>
                  </div>
                  <div>
                    <p className="text-xs text-muted-foreground">YTD Return</p>
                    <p className="text-sm font-semibold text-green-600 tabular-nums" data-testid="investment-ytd-return">
                      <ArrowUpRight className="h-3 w-3 inline" /> +{investment.ytd_return}%
                    </p>
                  </div>
                </div>
                <div className="mt-3 h-2 bg-gray-100 rounded-full overflow-hidden">
                  <div className="h-full bg-green-500 rounded-full" style={{ width: `${Math.min(investment.ytd_return * 10, 100)}%` }} />
                </div>
              </div>
            )}
          </div>

          {/* FX Rates Widget */}
          <div className="fx-widget" data-testid="fx-widget">
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-sm font-semibold tracking-tight" style={{ fontFamily: 'Space Grotesk' }}>Exchange Rates</h3>
              <Button variant="ghost" size="sm" className="text-xs text-[#0B5CAB]" onClick={() => navigate('/fx-rates')} data-testid="fx-open-full-page">
                View All <ChevronRight className="h-3 w-3 ml-1" />
              </Button>
            </div>
            <div className="space-y-0">
              {fxRates.slice(0, 6).map(rate => (
                <div key={rate.currency_code} className="flex items-center justify-between py-2 border-b border-gray-50 last:border-0">
                  <div className="flex items-center gap-2">
                    <span className="text-sm font-medium">{rate.currency_code}</span>
                    <span className="text-xs text-muted-foreground">{rate.name}</span>
                  </div>
                  <span className="text-sm tabular-nums text-muted-foreground">{rate.symbol}{rate.rate}</span>
                </div>
              ))}
            </div>
            <p className="text-[11px] text-muted-foreground mt-2"><Clock className="h-3 w-3 inline mr-1" />Updated just now</p>
          </div>
        </div>

        {/* Recent Transactions */}
        <div className="lg:col-span-3">
          <div className="flex items-center justify-between mb-3">
            <h3 className="text-sm font-semibold tracking-tight" style={{ fontFamily: 'Space Grotesk' }}>Recent Activity</h3>
            <Button variant="ghost" size="sm" className="text-xs text-[#0B5CAB]" onClick={() => navigate('/transactions')}>
              View All <ChevronRight className="h-3 w-3 ml-1" />
            </Button>
          </div>
          <div className="bg-white rounded-xl border p-4">
            {recentTxns.length === 0 ? (
              <div className="text-center py-8" data-testid="transactions-empty-state">
                <Clock className="h-8 w-8 text-muted-foreground mx-auto mb-2" />
                <p className="text-sm text-muted-foreground">No recent transactions</p>
                <Button variant="outline" size="sm" className="mt-3 text-xs" onClick={() => navigate('/transfer')}>Make a Transfer</Button>
              </div>
            ) : (
              <div className="space-y-3">
                {recentTxns.slice(0, 5).map(tx => (
                  <div key={tx._id} className="flex items-center justify-between py-2 border-b border-gray-50 last:border-0">
                    <div>
                      <p className="text-sm font-medium truncate max-w-[160px]">{tx.description}</p>
                      <p className="text-xs text-muted-foreground">{tx.category}</p>
                    </div>
                    <p className={`text-sm font-semibold tabular-nums ${tx.amount >= 0 ? 'amount-positive' : 'amount-negative'}`}>
                      {tx.amount >= 0 ? '+' : ''}{formatCurrency(Math.abs(tx.amount))}
                    </p>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

function getGreeting() {
  const h = new Date().getHours();
  if (h < 12) return 'morning';
  if (h < 17) return 'afternoon';
  return 'evening';
}
